import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, MapPin, Users, ArrowRight, AlertCircle, Plus, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { EventDetailSheet } from './EventDetailSheet';
import { QuickAddDialog } from './QuickAddDialog';
import type { CalendarEventDto } from '@cc/types';

/**
 * Today / Tomorrow calendar widget. Reads from the local mirror — the
 * 5-minute sync tick keeps it within 5 min of Google. Empty-state CTA
 * routes to /settings/accounts where Karl can connect calendar scope.
 */
export function DashboardCalendarWidget() {
  const window = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayAfterTomorrow = new Date(todayStart.getTime() + 2 * 24 * 60 * 60 * 1000);
    return {
      from: todayStart.toISOString(),
      to: dayAfterTomorrow.toISOString(),
    };
  }, []);

  const eventsQuery = useQuery({
    queryKey: qk.calendarEvents(window),
    queryFn: () => api.listCalendarEvents(window),
    refetchInterval: 5 * 60_000,
  });

  const statusQuery = useQuery({
    queryKey: qk.calendarStatus,
    queryFn: () => api.listCalendarAccountStatuses(),
  });

  const events = eventsQuery.data?.events ?? [];
  const statuses = statusQuery.data?.accounts ?? [];
  const anyConnected = statuses.some((s) => s.hasCalendarScope);

  const { today, tomorrow, needsRsvp, needsConfirm } = bucketEvents(events, statuses);
  const [selected, setSelected] = useState<CalendarEventDto | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">Calendar</h2>
          </div>
          <div className="flex items-center gap-2">
            {anyConnected && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => setAdding(true)}
              >
                <Plus className="h-3 w-3" /> New
              </Button>
            )}
            <Link
              to="/calendar"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {eventsQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : !anyConnected && statuses.length > 0 ? (
          <EmptyConnectCTA />
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No events on today or tomorrow.
          </p>
        ) : (
          <div className="space-y-4">
            {needsConfirm.length > 0 && (
              <DaySection
                label="Needs confirm (AI)"
                events={needsConfirm}
                emptyMessage=""
                onSelect={setSelected}
                icon={HelpCircle}
              />
            )}
            {needsRsvp.length > 0 && (
              <DaySection
                label="Needs RSVP"
                events={needsRsvp}
                emptyMessage=""
                onSelect={setSelected}
                icon={HelpCircle}
              />
            )}
            <DaySection
              label="Today"
              events={today}
              emptyMessage="Nothing on today."
              onSelect={setSelected}
            />
            <DaySection
              label="Tomorrow"
              events={tomorrow}
              emptyMessage="Nothing on tomorrow."
              muted
              onSelect={setSelected}
            />
          </div>
        )}

        {statuses.some((s) => s.lastSyncError) && (
          <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-destructive">
            <AlertCircle className="h-3 w-3" />
            A calendar reported a sync error. Check Settings.
          </p>
        )}
      </CardContent>
      <EventDetailSheet event={selected} onClose={() => setSelected(null)} />
      <QuickAddDialog open={adding} onOpenChange={setAdding} />
    </Card>
  );
}

function DaySection({
  label,
  events,
  emptyMessage,
  muted,
  onSelect,
  icon: Icon,
}: {
  label: string;
  events: CalendarEventDto[];
  emptyMessage: string;
  muted?: boolean;
  onSelect: (e: CalendarEventDto) => void;
  icon?: typeof HelpCircle;
}) {
  return (
    <div>
      <p
        className={`mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wider ${
          muted ? 'text-muted-foreground/70' : 'text-muted-foreground'
        }`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </p>
      {events.length === 0 ? (
        emptyMessage ? <p className="text-xs text-muted-foreground">{emptyMessage}</p> : null
      ) : (
        <ul className="space-y-1.5">
          {events.map((e) => (
            <EventRow key={e.id} event={e} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EventRow({
  event,
  onSelect,
}: {
  event: CalendarEventDto;
  onSelect: (e: CalendarEventDto) => void;
}) {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const timeLabel = event.isAllDay
    ? 'All day'
    : `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
  const isCancelled = event.status === 'CANCELLED';
  return (
    <li
      onClick={() => onSelect(event)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(event);
        }
      }}
      className={`flex cursor-pointer items-start gap-3 rounded-md border border-border/50 bg-card/50 px-3 py-2 transition-colors hover:border-foreground/20 hover:bg-card ${
        isCancelled ? 'opacity-50 line-through' : ''
      }`}
    >
      <div className="min-w-[5.5rem] flex-none">
        <p className="text-xs font-medium">{event.isAllDay ? 'All day' : format(start, 'h:mm a')}</p>
        {!event.isAllDay && (
          <p className="text-[10px] text-muted-foreground">{format(end, 'h:mm a')}</p>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 truncate text-sm font-medium">
            {event.summary || '(no title)'}
          </p>
          <SourceBadge source={event.source} />
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {event.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {event.location}
            </span>
          )}
          {event.attendees.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" /> {event.attendees.length}
            </span>
          )}
          {!event.location && !event.attendees.length && (
            <span aria-hidden className="sr-only">
              {timeLabel}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function SourceBadge({ source }: { source: CalendarEventDto['source'] }) {
  if (source === 'GOOGLE') {
    return (
      <Badge variant="outline" className="flex-none text-[10px]">
        Google
      </Badge>
    );
  }
  if (source === 'EMAIL_INVITE') {
    return (
      <Badge variant="outline" className="flex-none text-[10px]">
        Invite
      </Badge>
    );
  }
  return (
    <Badge variant="warning" className="flex-none text-[10px]">
      Auto
    </Badge>
  );
}

function EmptyConnectCTA() {
  return (
    <div className="space-y-2 py-2">
      <p className="text-xs text-muted-foreground">
        No connected accounts have granted calendar access.
      </p>
      <Link
        to="/settings/accounts"
        className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
      >
        Connect calendar <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function bucketEvents(
  events: CalendarEventDto[],
  _statuses: { accountId: string; email: string }[],
): {
  today: CalendarEventDto[];
  tomorrow: CalendarEventDto[];
  needsRsvp: CalendarEventDto[];
  needsConfirm: CalendarEventDto[];
} {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
  const dayAfter = new Date(todayStart.getTime() + 2 * 24 * 60 * 60 * 1000);

  const today: CalendarEventDto[] = [];
  const tomorrow: CalendarEventDto[] = [];
  const needsRsvp: CalendarEventDto[] = [];
  const needsConfirm: CalendarEventDto[] = [];
  for (const e of events) {
    const start = new Date(e.startTime);
    if (start < now && start < todayStart) continue;
    if (!e.userConfirmed) {
      // Phase E: GOOGLE / EMAIL_INVITE with needsAction RSVP from self attendee
      // Phase H: AI_COMMITMENT extracted at 0.5-0.85 confidence
      if (e.source === 'AI_COMMITMENT') needsConfirm.push(e);
      else needsRsvp.push(e);
      continue;
    }
    if (start >= todayStart && start < tomorrowStart) today.push(e);
    else if (start >= tomorrowStart && start < dayAfter) tomorrow.push(e);
  }
  return { today, tomorrow, needsRsvp, needsConfirm };
}
